import type { DashboardId, DeviationCategory } from '@clinspace/shared';
import { DEVIATION_CATEGORIES } from '@clinspace/shared';
import { Dashboard, type ViewRegion } from '../Dashboard.ts';
import type { CanvasPanel } from '../CanvasPanel.ts';
import { PALETTE, SERIES, bars, list, treemap, type Rect } from '../charts.ts';
import { deviationDetail } from '../details.ts';
import { hoveredRow } from './Enrollment.ts';

/** Protocol Deviation: totals, major vs minor, treemap by category, bars by site; category -> deviations. */
export class ProtocolDeviationDashboard extends Dashboard {
  readonly id: DashboardId = 'ProtocolDeviation';

  protected override segmentLabel(segment: string): string {
    return segment.replace(/^(category|deviation):/, '');
  }

  protected drawContent(ctx: CanvasRenderingContext2D, panel: CanvasPanel, area: Rect): void {
    const [category, deviation] = this.path;
    if (category && deviation) {
      const d = this.view.deviation(deviation.slice(10));
      if (d) {
        this.detail = deviationDetail(this.view, d);
        this.drawDetail(ctx, panel, area, this.detail);
      }
      return;
    }
    this.detail = undefined;
    const devs = this.view.deviations(this.filters);
    if (category) {
      const cat = category.slice(9) as DeviationCategory;
      const items = devs
        .filter((d) => d.category === cat)
        .sort((a, b) => b.date.localeCompare(a.date));
      const rows = list(
        ctx,
        ['Subject', 'Site', 'Date', 'Class', 'Description', 'Status'],
        items.map((d) => ({
          cells: [
            d.subjectId,
            this.view.siteLabel(d.siteId),
            d.date,
            d.classification ?? 'Unclassified',
            d.description,
            d.status,
          ],
          flag: d.classification === null || d.classification === 'Major' ? 'x' : undefined,
          colour: d.classification === null ? PALETTE.amber : PALETTE.crimson,
        })),
        area,
        {
          title: `${cat} · ${items.length} deviations`,
          widths: [0.13, 0.2, 0.12, 0.12, 0.33, 0.1],
          hoveredIndex: hoveredRow(panel, 'row:'),
          rowH: 20,
        },
      );
      rows.forEach((r, i) =>
        panel.region<ViewRegion>({
          id: `row:${i}`,
          ...r,
          data: { kind: 'drill', segment: `deviation:${items[i]!.id}` },
          tooltip: [items[i]!.description, 'Open the deviation'],
        }),
      );
      return;
    }
    const major = devs.filter((d) => d.classification === 'Major').length;
    const minor = devs.filter((d) => d.classification === 'Minor').length;
    const unclassified = devs.filter((d) => d.classification === null).length;
    this.kpis(
      panel,
      ctx,
      [
        { label: 'Total', value: String(devs.length) },
        { label: 'Major', value: String(major), colour: PALETTE.crimson },
        { label: 'Minor', value: String(minor), colour: PALETTE.amber },
        {
          label: 'Unclassified',
          value: String(unclassified),
          colour: unclassified ? PALETTE.amber : PALETTE.teal,
          sub: unclassified ? 'need classification' : 'all classified',
        },
        { label: 'Open', value: String(devs.filter((d) => d.status === 'Open').length) },
      ],
      { x: area.x, y: area.y, w: area.w, h: 76 },
    );
    const left: Rect = { x: area.x, y: area.y + 88, w: area.w * 0.58, h: area.h - 88 };
    const byCat = DEVIATION_CATEGORIES.map((c, i) => ({
      label: c,
      value: devs.filter((d) => d.category === c).length,
      colour: SERIES[i % SERIES.length]!,
    }));
    const cells = treemap(ctx, byCat, left, {
      title: 'Deviations by category',
      hoveredIndex: hoveredRow(panel, 'cat:'),
    });
    cells.forEach((c, i) => {
      const cat = c.item.label as DeviationCategory;
      const items = devs.filter((d) => d.category === cat);
      panel.region<ViewRegion>({
        id: `cat:${i}`,
        x: c.x,
        y: c.y,
        w: c.w,
        h: c.h,
        data: { kind: 'drill', segment: `category:${cat}` },
        tooltip: [
          cat,
          `${items.length} total · ${items.filter((d) => d.classification === 'Major').length} major · ${items.filter((d) => d.classification === null).length} unclassified`,
          'Open the list',
        ],
      });
    });
    const right: Rect = {
      x: area.x + area.w * 0.62,
      y: area.y + 88,
      w: area.w * 0.38,
      h: area.h - 88,
    };
    const sites = this.view.file.sites;
    const data = sites.map((s) => ({
      label: s.city,
      values: [
        devs.filter((d) => d.siteId === s.id && d.classification === 'Major').length,
        devs.filter((d) => d.siteId === s.id && d.classification === 'Minor').length,
        devs.filter((d) => d.siteId === s.id && d.classification === null).length,
      ],
      colours: [PALETTE.crimson, PALETTE.amber, PALETTE.grey],
    }));
    const rects = bars(ctx, data, right, {
      stacked: true,
      title: 'By site (major / minor / unclassified)',
      hoveredIndex: hoveredRow(panel, 'site:'),
    });
    rects.forEach((r, i) =>
      panel.region<ViewRegion>({
        id: `site:${i}`,
        ...r,
        data: { kind: 'filter', filterKey: 'siteId' },
        tooltip: [
          this.view.siteLabel(sites[i]!.id),
          `major ${data[i]!.values[0]} · minor ${data[i]!.values[1]} · unclassified ${data[i]!.values[2]}`,
          'Filter by site',
        ],
      }),
    );
  }
}
